import type { Card, Suit } from '../../engine/card.js';
import { color } from '../../engine/card.js';
import type { GameState, Pile } from '../../engine/game-state.js';
import { topCard } from '../../engine/game-state.js';
import type { Move } from '../../engine/moves.js';
import type { SolitaireRules } from '../../engine/rules.js';

const TABLEAU_IDS = ['t1', 't2', 't3', 't4', 't5', 't6', 't7'] as const;
const FOUNDATION_IDS = ['f-spades', 'f-hearts', 'f-diamonds', 'f-clubs'] as const;
const FOUNDATION_SUIT: Record<(typeof FOUNDATION_IDS)[number], Suit> = {
  'f-spades': 'spades',
  'f-hearts': 'hearts',
  'f-diamonds': 'diamonds',
  'f-clubs': 'clubs',
};

function emptyPile(id: string, type: Pile['type']): Pile {
  return { id, type, cards: [] };
}

function setupKlondike(deck: Card[]): GameState {
  const piles: Record<string, Pile> = {};
  for (const id of TABLEAU_IDS) piles[id] = emptyPile(id, 'tableau');
  for (const id of FOUNDATION_IDS) piles[id] = emptyPile(id, 'foundation');
  piles.stock = emptyPile('stock', 'stock');
  piles.waste = emptyPile('waste', 'waste');

  const d = deck.slice();
  for (let i = 0; i < TABLEAU_IDS.length; i++) {
    for (let j = i; j < TABLEAU_IDS.length; j++) {
      const card = d.shift()!;
      card.faceUp = j === i;
      piles[TABLEAU_IDS[j]!]!.cards.push(card);
    }
  }
  for (const c of d) {
    c.faceUp = false;
    piles.stock!.cards.push(c);
  }

  return { piles, movesCount: 0 };
}

function isFoundationFor(pile: Pile, suit: Suit): boolean {
  return pile.type === 'foundation' && FOUNDATION_SUIT[pile.id as keyof typeof FOUNDATION_SUIT] === suit;
}

function canPlaceOnFoundation(pile: Pile, card: Card): boolean {
  if (!isFoundationFor(pile, card.suit)) return false;
  const top = topCard(pile);
  if (!top) return card.rank === 1;
  return card.rank === top.rank + 1;
}

function canPlaceOnTableau(pile: Pile, card: Card): boolean {
  if (pile.type !== 'tableau') return false;
  const top = topCard(pile);
  if (!top) return card.rank === 13;
  if (!top.faceUp) return false;
  return color(card) !== color(top) && card.rank === top.rank - 1;
}

function isMovableSequence(cards: Card[]): boolean {
  if (cards.length === 0) return false;
  if (!cards[0]!.faceUp) return false;
  for (let i = 1; i < cards.length; i++) {
    const a = cards[i - 1]!;
    const b = cards[i]!;
    if (!b.faceUp) return false;
    if (color(a) === color(b)) return false;
    if (a.rank !== b.rank + 1) return false;
  }
  return true;
}

export const klondikeRules: SolitaireRules = {
  id: 'klondike',

  setup(deck) {
    return setupKlondike(deck);
  },

  canMove(state, move: Move) {
    const from = state.piles[move.from];
    const to = state.piles[move.to];
    if (!from || !to) return false;
    if (from === to) return false;

    const idx = from.cards.findIndex((c) => c.id === move.cardId);
    if (idx === -1) return false;
    const moving = from.cards.slice(idx);

    if (from.type === 'stock') {
      return to.type === 'waste' && moving.length === 1;
    }

    const head = moving[0]!;
    if (!head.faceUp) return false;

    if (to.type === 'foundation') {
      return moving.length === 1 && canPlaceOnFoundation(to, head);
    }
    if (to.type === 'tableau') {
      if (!isMovableSequence(moving)) return false;
      return canPlaceOnTableau(to, head);
    }
    return false;
  },

  isWon(state) {
    return FOUNDATION_IDS.every((id) => state.piles[id]!.cards.length === 13);
  },

  afterMove(state, move) {
    const from = state.piles[move.from];
    if (from && from.type === 'tableau') {
      const top = topCard(from);
      if (top && !top.faceUp) top.faceUp = true;
    }
  },
};

export function hasAnyNonStockMove(state: GameState): boolean {
  const pileIds = Object.keys(state.piles);
  for (const fromPile of Object.values(state.piles)) {
    if (fromPile.type === 'stock') continue;
    for (const card of fromPile.cards) {
      if (!card.faceUp) continue;
      for (const toId of pileIds) {
        if (toId === fromPile.id || toId === 'stock') continue;
        if (klondikeRules.canMove(state, { from: fromPile.id, to: toId, cardId: card.id })) {
          return true;
        }
      }
    }
  }
  return false;
}

export function drawFromStock(state: GameState, count: number = 1): void {
  const stock = state.piles.stock!;
  const waste = state.piles.waste!;
  if (stock.cards.length === 0) {
    while (waste.cards.length > 0) {
      const c = waste.cards.pop()!;
      c.faceUp = false;
      stock.cards.push(c);
    }
    return;
  }
  const n = Math.min(count, stock.cards.length);
  for (let i = 0; i < n; i++) {
    const c = stock.cards.pop()!;
    c.faceUp = true;
    waste.cards.push(c);
  }
}
